<script setup lang="ts">
import { computed } from 'vue'

import clsx from 'clsx'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import draggable from 'vuedraggable'

import modal from '@/services/modal'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'

const props = defineProps<{
    element: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    elementParent?: any
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()

const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const content = computed(() => props.element.frameContent?.content ?? [])
const isCustomParentBackgroundColor = computed(() => props.elementParent?.extra?.background_color && props.elementParent?.extra?.background_color !== '')
const backgroundColor = computed(() => {
    if (isCustomParentBackgroundColor.value) return props.elementParent.extra.background_color

    return editorPageStore.getSectionExtra(props.sectionIndex)?.background_color || props.sectionBackgroundColor
})
const textColor = computed(() => layoutStore.getAdaptiveTextColor(backgroundColor.value))
const dynamicStyle = computed(() => ({ color: textColor.value, stroke: textColor.value }))

const updateFaqContent = (asks: any[]) => {
    betaStore.patchElementBySchema(props.schema, {
        frameContent: {
            content: [...asks],
        },
    })
}

const onChangeAskTitle = (index: number, title: string) => {
    const asks = [...content.value]
    asks[index] = { ...asks[index], title }
    updateFaqContent(asks)
}

const onChangeAskText = (index: number, text: string) => {
    const asks = [...content.value]
    asks[index] = { ...asks[index], text }
    updateFaqContent(asks)
}

const onClickNewAsk = () => {
    updateFaqContent([
        ...content.value,
        {
            name: content.value.length,
            open: false,
            text: 'Resposta',
            title: 'Pergunta',
            type: 'text',
        },
    ])
}

const onRemoveAsk = async (index: number) => {
    const result = await modal.confirmation('Confirmar Exclusão', 'Deseja realmente excluir esta pergunta?', {
        type: 'danger',
        textConfirm: 'Sim, excluir',
        textCancel: 'Não, voltar',
    })

    if (!result) return

    const asks = [...content.value]
    asks.splice(index, 1)
    updateFaqContent(asks)
}

const onEndDraggable = () => {
    updateFaqContent([...content.value])
}
</script>

<template>
    <div
        tabindex="0"
        :id="'betaEditorFaq-' + elementIndex"
        :class="clsx('editor-faq-beta')"
        :data-active="isActive"
        :schema="JSON.stringify(schema)"
        :style="dynamicStyle"
    >
        <div class="space-y-6 p-4">
            <h2 class="text-lg font-bold">Perguntas Cadastradas</h2>

            <draggable
                :list="content"
                item-key="index"
                handle=".handle-drag"
                class="space-y-6"
                @change="onEndDraggable"
            >
                <template #item="{ element, index }">
                    <Disclosure>
                        <div class="w-full rounded-lg border-[1.5px] border-[#F5F5F5] p-6">
                            <DisclosureButton class="flex w-full items-center justify-between">
                                <div class="flex items-center space-x-2">
                                    <button class="handle-drag cursor-ns-resize" type="button">
                                        <svg class="h-8 w-8 stroke-[var(--main-color)]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M15.6 15.6L12 19.2L8.39999 15.6M8.39999 8.39999L12 4.79999L15.6 8.39999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </button>
                                    <span>{{ element.title }}</span>
                                </div>

                                <button type="button" :style="dynamicStyle" @click.stop="onRemoveAsk(index)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none" stroke="currentColor">
                                        <path d="M0.888885 3.82353H15.1111M5.33333 1H10.6667M11.1111 17H4.88889C3.90705 17 3.11111 16.1572 3.11111 15.1176L2.70524 4.80389C2.6842 4.26918 3.08792 3.82353 3.59336 3.82353H12.4066C12.9121 3.82353 13.3158 4.26918 13.2948 4.80389L12.8889 15.1176C12.8889 16.1572 12.0929 17 11.1111 17Z" stroke-width="1.5" stroke-linecap="round" />
                                    </svg>
                                </button>
                            </DisclosureButton>

                            <DisclosurePanel class="w-full space-y-6 px-10">
                                <div class="mt-6 flex flex-col">
                                    <label class="text-sm font-semibold">Título</label>
                                    <input v-model="element.title" type="text" class="rounded border-2 border-[#F5F5F5] bg-transparent px-4 py-3 outline-none" placeholder="Ex: Quais são as formas de pagamento?" @input="(event: any) => onChangeAskTitle(index, event.target.value)">
                                </div>

                                <div class="flex flex-col">
                                    <label class="text-sm font-semibold">Descrição</label>
                                    <input v-model="element.text" type="text" class="rounded border-2 border-[#F5F5F5] bg-transparent px-4 py-3 outline-none" placeholder="Ex: Aceitamos Cartão, Dinheiro..." @input="(event: any) => onChangeAskText(index, event.target.value)">
                                </div>
                            </DisclosurePanel>
                        </div>
                    </Disclosure>
                </template>
            </draggable>

            <button type="button" class="flex items-center justify-center rounded-full border-2 border-[var(--main-color)] px-6 py-3" @click="onClickNewAsk">
                <span class="mr-2 text-base font-bold text-[var(--main-color)]">Nova pergunta</span>
                <svg class="h-6 w-6 stroke-[var(--main-color)]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 7.19995L12 16.8M16.8 12L7.2 12" stroke-width="2" stroke-linecap="round" />
                </svg>
            </button>
        </div>
    </div>
</template>

<style scoped>
.editor-faq-beta {
    @apply relative my-3 border-2 border-transparent bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-faq-beta[data-active='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}
</style>
